import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Newspaper, Sparkles, RefreshCw, AlertTriangle, Calendar } from 'lucide-react';
import { fetchJSON as getJSON } from '../lib/api-client';
import { WideScanPanel } from './WideScanPanel';
import { CollapsibleSection } from './CollapsibleSection';

interface IndexSnapshot {
  symbol: string;
  name: string;
  price?: number;
  change?: number;
  changePct?: number;
}

interface ReportHighlight {
  symbol: string;
  name?: string;
  rationale: string;
  setup: string;
  source: 'decision' | 'portfolio' | 'watchlist' | 'top3' | 'catalysts';
  confidence?: number;
}

interface ReviewStats {
  total: number;
  hits: number;
  misses: number;
  working: number;
  expired: number;
  pending: number;
  resolved: number;
  hitRate: number;
}

interface ThesisReviewItem {
  thesisId: string;
  symbol: string;
  status: string;
  entry: number;
  stop: number;
  t1: number;
  t2: number;
  quote?: number;
  pnlPct?: number;
  outcome: 'hit' | 'miss' | 'working' | 'expired' | 'pending';
  note: string;
}

interface EodReviewSection {
  date: string;
  items: ThesisReviewItem[];
  today: ReviewStats;
  weekly: ReviewStats;
}

interface ReportActionSnapshot {
  symbol: string;
  action: string;
  actionStatus: string;
  entry?: number;
  stop?: number;
  t1?: number;
  t2?: number;
  confidence: number;
  quantity: number;
  quantityUnit: string;
  notional: number;
  maxLossAmount: number;
  expectedNetReturn?: [number, number];
  executionSummary: string;
  reasonsTop3: string[];
  failureConditions: string[];
  source: 'portfolio' | 'new-pick' | 'watchlist';
}

interface TodayActionSection {
  planDate: string;
  marketAction: 'trade' | 'no-trade' | 'manage-existing-only';
  reason: string;
  expectedNetReturnRange?: [number, number];
  cashAfterPlannedTrades: Partial<Record<'USD' | 'TWD', number>>;
  warnings: string[];
  actions: ReportActionSnapshot[];
}

interface SymbolAccuracy {
  symbol: string;
  total: number;
  resolved: number;
  hits: number;
  misses: number;
  pending: number;
  expired: number;
  hitRate: number;
  avgNetPnlPct?: number;
  lastOutcome?: string;
}

interface PenguinPerformanceSummary {
  days: number;
  overall: ReviewStats;
  today: ReviewStats;
  bySymbol: SymbolAccuracy[];
  shadow: {
    total: number;
    entered: number;
    closed: number;
    winRate: number;
    avgNetPnlPct: number;
    maxDrawdownPct: number;
  };
  sampleNote: string;
}

interface NarrativeNote {
  symbol: string;
  note: string;
}

interface ReportNarrative {
  marketCommentary: string;
  bottomLine: string;
  reviewNotes: NarrativeNote[];
  actionNotes: NarrativeNote[];
  watchlistNotes: NarrativeNote[];
  modelUsed: string;
  fallbackUsed: boolean;
  generatedAt: string;
}

interface DailyReport {
  id: string;
  date: string;
  generatedAt: string;
  modelUsed: string;
  indices: IndexSnapshot[];
  marketSummary: string;
  highlights: ReportHighlight[];
  watchTomorrow: string[];
  review?: EodReviewSection;
  todayAction?: TodayActionSection;
  performance?: PenguinPerformanceSummary;
  narrative?: ReportNarrative;
}

interface ReportListItem {
  id: string;
  date: string;
  generatedAt: string;
  modelUsed: string;
  marketSummary: string;
  highlightCount: number;
}

export function DailyReportPanel({ onSelectSymbol }: { onSelectSymbol?: (s: string) => void }) {
  const [pickedDate, setPickedDate] = useState<string | null>(null);

  // 最近 14 天清單
  const recent = useQuery({
    queryKey: ['reports-recent', 14],
    queryFn: () => getJSON<{ reports: ReportListItem[]; count: number }>('/api/reports/recent?days=14'),
    refetchInterval: 10 * 60_000,
  });

  // 當前選的 / 最新的報告
  const reportQuery = useQuery({
    queryKey: ['report', pickedDate ?? 'latest'],
    queryFn: () => {
      const url = pickedDate
        ? `/api/report?date=${pickedDate}`
        : '/api/report/latest';
      return getJSON<DailyReport>(url);
    },
    refetchInterval: 10 * 60_000,
    retry: false, // 若沒有報告就直接顯示沒有
  });

  const generateMut = useMutation({
    mutationFn: () => getJSON<DailyReport>('/api/report/generate', { method: 'POST' }),
    onSuccess: () => {
      reportQuery.refetch();
      recent.refetch();
    },
  });

  const report = reportQuery.data;
  // 明日觀察過濾掉系統警告（llm-fallback-used、DailyPlan warning 等）——
  // 它們已在「盤後行動結語」的系統提醒列出，不該再重複當成「要觀察的事」。
  const watchTomorrow = (report?.watchTomorrow ?? []).filter((w) => !/warning|fallback|dailyplan/i.test(w));

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <span className="text-[11px] font-semibold text-(--text-muted) flex items-center gap-1">
          <Newspaper className="h-3.5 w-3.5" />每天 06:45 TWT 自動產生·可手動重產
        </span>
        <div className="flex items-center gap-1 flex-wrap">
          {recent.data && recent.data.reports.length > 0 && (
            <select
              aria-label="選擇歷史報告"
              value={pickedDate ?? ''}
              onChange={(e) => setPickedDate(e.target.value || null)}
              className="h-7 rounded border border-(--border) bg-(--bg-base) px-1.5 text-[11px]"
            >
              <option value="">最新</option>
              {recent.data.reports.map((r) => (
                <option key={r.date} value={r.date}>{r.date}</option>
              ))}
            </select>
          )}
          <button
            type="button"
            onClick={() => generateMut.mutate()}
            disabled={generateMut.isPending}
            className="h-7 inline-flex items-center gap-1 rounded border border-(--accent)/40 bg-(--accent)/15 px-2 text-[11px] text-(--accent) hover:bg-(--accent)/25 disabled:opacity-50"
            title="手動產生今日報告（耗時 1~2 分鐘）"
          >
            <RefreshCw className={`h-3 w-3 ${generateMut.isPending ? 'animate-spin' : ''}`} />
            {generateMut.isPending ? '生成中…' : '手動生成'}
          </button>
        </div>
      </div>

      {reportQuery.isLoading && (
        <div className="text-(--text-muted) text-sm py-6 text-center">載入中…</div>
      )}

      {reportQuery.error && (
        <div className="p-3 rounded border border-(--warn)/40 bg-(--warn)/10 text-[11px] flex items-start gap-1.5">
          <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0 text-(--warn)" />
          <div>
            <div className="font-semibold text-(--warn) mb-1">尚無報告</div>
            <div className="text-(--text-secondary)">
              第一份報告會在每天 06:45 TWT (22:45 UTC) 自動產生。
              想立刻看？按「手動生成」（會刷新交易計畫 / 紙上單，並彙整今日行動與準確率）。
            </div>
          </div>
        </div>
      )}

      {generateMut.error && (
        <div className="mb-2 text-[11px] text-(--up) p-2 rounded border border-(--up)/40 bg-(--up)/10">
          生成失敗：{(generateMut.error as Error).message}
        </div>
      )}

      {report && (
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-(--text-muted) text-[10px] flex-wrap">
            <Calendar className="h-3 w-3" />
            <span className="font-semibold text-(--text-secondary)">{report.date}</span>
            <span className="px-1.5 py-0.5 rounded bg-(--bg-base) border border-(--border)">
              {report.modelUsed.startsWith('facts-only') ? '純數據彙整' : report.modelUsed}
            </span>
          </div>

          {/* 重點摘要列（Hero）：整份報告最關鍵的 4 個結論放最上面一眼看完 */}
          {(report.todayAction || report.review) && <ReportHero report={report} />}

          {/* 大盤指數：6 個用 grid-cols-3 / lg:6 整齊網格，不要 4+2 空格 */}
          {report.indices.length > 0 && (
            <section>
              <ReportHeading icon="📈" title="大盤" />
              <div className="grid grid-cols-3 lg:grid-cols-6 gap-2">
                {report.indices.map((i) => (
                  <IndexCard key={i.symbol} idx={i} />
                ))}
              </div>
            </section>
          )}

          {/* 盤後總評：有 LLM 敘事就顯示交易員口吻短評 + 一句結語；
              沒有敘事（quota 用完走 facts-only）才退回原本的四行數據濃縮。 */}
          {report.narrative ? (
            <div className="p-3 rounded-lg bg-gradient-to-br from-(--ice) to-(--bg-base) border border-(--accent)/30 text-[12px] text-(--text-primary) leading-relaxed">
              <div className="text-[10px] uppercase tracking-wider text-(--accent-deep) font-bold mb-1 flex items-center gap-1">
                <Sparkles className="h-3 w-3 text-(--accent)" />企鵝盤後總評
              </div>
              <div className="whitespace-pre-line">{report.narrative.marketCommentary}</div>
              {report.narrative.bottomLine && (
                <div className="mt-2 pt-2 border-t border-(--border) text-[12px] font-semibold text-(--accent-deep)">
                  👉 {report.narrative.bottomLine}
                </div>
              )}
            </div>
          ) : (
            report.marketSummary && !(report.modelUsed.startsWith('facts-only') && (report.review || report.todayAction)) && (
              <div className="p-3 rounded bg-(--bg-base) border border-(--border) text-[12px] text-(--text-primary) leading-relaxed whitespace-pre-line">
                <div className="text-[10px] uppercase tracking-wider text-(--accent-deep) font-bold mb-1 flex items-center gap-1">
                  <Sparkles className="h-3 w-3 text-(--accent)" />盤後總結
                </div>
                {report.marketSummary}
              </div>
            )
          )}

          {report.review && <ReviewSection review={report.review} notes={report.narrative?.reviewNotes} onSelectSymbol={onSelectSymbol} />}

          {report.todayAction && (
            <TodayActionReportSection section={report.todayAction} narrative={report.narrative} onSelectSymbol={onSelectSymbol} />
          )}

          {/* highlights */}
          {!report.todayAction && report.highlights.length > 0 && (
            <section>
              <ReportHeading icon="🎯" title={`重點關注（${report.highlights.length}）`} />
              <ul className="space-y-1.5">
                {report.highlights.map((h, i) => (
                  <li key={i} className="p-2 rounded bg-(--bg-base) border border-(--border)">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <button
                        type="button"
                        onClick={() => onSelectSymbol?.(h.symbol)}
                        className="font-bold text-sm text-(--accent) hover:underline"
                        title="切換到此標的"
                      >
                        {h.symbol}
                      </button>
                      {h.name && <span className="text-[11px] text-(--text-muted)">{h.name}</span>}
                      <span className={`text-[10px] px-1.5 py-0.5 rounded border ${
                        h.source === 'top3' || h.source === 'decision' ? 'border-(--accent)/40 text-(--accent)' : 'border-(--up)/40 text-(--up)'
                      }`}>
                        {sourceLabel(h.source)}
                      </span>
                      <span className="text-[10px] text-(--text-secondary) px-1.5 py-0.5 rounded border border-(--border)">
                        {h.setup}
                      </span>
                      {h.confidence != null && (
                        <span className="ml-auto text-[10px] font-bold tnum text-(--text-secondary)">
                          {Math.round(h.confidence * 100)}%
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-(--text-secondary) leading-relaxed">{h.rationale}</div>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* watchTomorrow */}
          {watchTomorrow.length > 0 && (
            <section>
              <ReportHeading icon="🔭" title={`明日觀察（${watchTomorrow.length}）`} />
              <ul className="space-y-1 pl-1">
                {watchTomorrow.map((w, i) => (
                  <li key={i} className="text-[11px] text-(--text-secondary) flex items-start gap-1.5">
                    <span className="text-(--accent) mt-0.5">◆</span>
                    {w}
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* 全市場廣掃：盤後性質，收在報告末尾預設折起，要看詳細再展開 */}
          <CollapsibleSection
            storageKey="daily-report-wide-scan"
            defaultCollapsed
            title={<span className="text-sm font-semibold text-(--accent)">🔭 全市場廣掃</span>}
            subtitle={<span className="text-[10px] text-(--text-muted)">台股全市場形態 · 挖出池外冷門股 · 已驗證關聯</span>}
          >
            <div className="p-3">
              <WideScanPanel onSelectSymbol={onSelectSymbol} />
            </div>
          </CollapsibleSection>
        </div>
      )}
    </div>
  );
}

function pct(value?: number) {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '—';
  const n = value * 100;
  return `${n >= 0 ? '+' : ''}${n.toFixed(1)}%`;
}

// expectedReturn / expectedNetReturn 已是「百分點」（3.9 = 3.9%），不要再 * 100（會變 390%）。
function pctPoints(value?: number) {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '—';
  return `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`;
}

function money(value?: number) {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '—';
  return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function marketActionLabel(action: TodayActionSection['marketAction']): string {
  if (action === 'trade') return '可進場';
  if (action === 'no-trade') return '今日不開新倉';
  return '只管理現有部位';
}

function actionLabel(action: string): string {
  const map: Record<string, string> = {
    buy: '買進', add: '加碼', hold: '續抱', reduce: '減碼', exit: '出場', wait: '觀望', avoid: '避開',
  };
  return map[action] ?? action;
}

function actionStatusLabel(status: string): string {
  const map: Record<string, string> = {
    'pending-trigger': '等觸發', triggered: '已觸發', adopted: '已採納', entered: '已進場',
    cancelled: '取消', expired: '過期', 'stale-data-warning': '資料過舊', 'no-trade': '不下單',
  };
  return map[status] ?? status;
}

function quantityUnitLabel(unit: string): string {
  if (unit === 'odd-lot') return '零股';
  if (unit === 'lot') return '張';
  if (unit === 'fractional-share') return '碎股';
  return '股';
}

function thesisStatusLabel(status: string): string {
  const map: Record<string, string> = {
    'pending-fill': '待成交', open: '持有中', stopped: '停損', t1_hit: 'T1 達標', t2_hit: 'T2 達標',
    thesis_broken: '交易假設破壞', manually_closed: '手動關閉', expired: '過期',
  };
  return map[status] ?? status;
}

function sourceLabel(source: ReportHighlight['source']) {
  if (source === 'decision') return '🐧 決策';
  if (source === 'portfolio') return '📊 部位';
  if (source === 'watchlist') return '⏸ 觀望';
  return source === 'top3' ? '📊 TOP3' : '📰 事件';
}

function outcomeBadge(outcome: ThesisReviewItem['outcome']) {
  const map: Record<ThesisReviewItem['outcome'], { label: string; cls: string }> = {
    hit: { label: '命中', cls: 'border-(--up)/40 text-(--up)' },
    miss: { label: '失準', cls: 'border-(--down)/40 text-(--down)' },
    working: { label: '進行中', cls: 'border-(--accent)/40 text-(--accent)' },
    expired: { label: '未觸發', cls: 'border-(--text-muted)/40 text-(--text-muted)' },
    pending: { label: '待成交', cls: 'border-(--warn)/40 text-(--warn)' },
  };
  return map[outcome];
}

function ReviewSection({ review, notes, onSelectSymbol }: { review: EodReviewSection; notes?: NarrativeNote[]; onSelectSymbol?: (s: string) => void }) {
  const noteFor = (symbol: string) => notes?.find((n) => n.symbol === symbol)?.note;
  return (
    <section>
      <ReportHeading icon="🔙" title={`昨日回顧（${review.items.length}）`} />
      {review.items.length === 0 ? (
        <div className="text-[11px] text-(--text-muted)">今天沒有交易計畫盤後評估紀錄；等下次採納或紙上單結案後會累積。</div>
      ) : (
        <ul className="space-y-1.5">
          {review.items.slice(0, 6).map((item) => {
            const badge = outcomeBadge(item.outcome);
            const aiNote = noteFor(item.symbol);
            return (
              <li key={item.thesisId} className="rounded border border-(--border) bg-(--bg-card) p-2">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <button type="button" onClick={() => onSelectSymbol?.(item.symbol)} className="font-bold text-sm text-(--accent) hover:underline">
                    {item.symbol}
                  </button>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded border ${badge.cls}`}>{badge.label}</span>
                  <span className="text-[10px] text-(--text-muted)">{thesisStatusLabel(item.status)}</span>
                  <span className={`ml-auto text-[11px] font-bold tnum ${item.pnlPct != null && item.pnlPct >= 0 ? 'text-(--up)' : 'text-(--down)'}`}>
                    {pct(item.pnlPct)}
                  </span>
                </div>
                <div className="text-[11px] text-(--text-secondary) leading-relaxed">
                  進 {item.entry} / 停 {item.stop} / T1 {item.t1} / T2 {item.t2}
                  {item.quote != null && <span> · 收/現 {item.quote}</span>}
                  <br />{item.note}
                </div>
                {aiNote && (
                  <div className="mt-1 pt-1 border-t border-(--border) text-[11px] text-(--accent-deep) leading-relaxed flex items-start gap-1">
                    <span className="shrink-0">🐧</span><span>{aiNote}</span>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function TodayActionReportSection({ section, narrative, onSelectSymbol }: { section: TodayActionSection; narrative?: ReportNarrative; onSelectSymbol?: (s: string) => void }) {
  const actionable = section.actions.filter((a) => a.source !== 'watchlist');
  const watchlist = section.actions.filter((a) => a.source === 'watchlist');
  const actionNoteFor = (symbol: string) => narrative?.actionNotes.find((n) => n.symbol === symbol)?.note;
  const watchNoteFor = (symbol: string) => narrative?.watchlistNotes.find((n) => n.symbol === symbol)?.note;
  return (
    <section>
      <ReportHeading icon="📋" title={`明日進場計畫（${marketActionLabel(section.marketAction)}）`} />
      <div className="text-[10px] text-(--text-muted) mb-2 leading-relaxed">
        以下是<b className="text-(--text-secondary)">明天開盤掛單、等觸發價才成交</b>的計畫（不是已經買進）；右側百分比＝AI 信心。
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-0.5 mb-2 text-[10px] text-(--text-muted)">
        <span>USD 剩餘：約 {money(section.cashAfterPlannedTrades.USD)}</span>
        <span>TWD 剩餘：約 {money(section.cashAfterPlannedTrades.TWD)}</span>
      </div>
      {actionable.length === 0 ? (
        <div className="text-[11px] text-(--text-muted) leading-relaxed">{section.reason || '今天沒有可執行新單；企鵝建議坐好，不要亂按。'}</div>
      ) : (
        <ul className="space-y-1.5">
          {actionable.slice(0, 5).map((item) => <ActionSnapshotItem key={`${item.source}:${item.symbol}:${item.action}`} item={item} aiNote={actionNoteFor(item.symbol)} onSelectSymbol={onSelectSymbol} />)}
        </ul>
      )}
      {watchlist.length > 0 && (
        <div className="mt-2 pt-2 border-t border-(--border)">
          <div className="text-[10px] text-(--text-muted) mb-1">觀望清單（不進場，在等條件成熟）</div>
          <ul className="space-y-1">
            {watchlist.map((item) => {
              const note = watchNoteFor(item.symbol);
              return (
                <li key={`${item.symbol}:${item.action}`} className="text-[11px] leading-relaxed">
                  <button type="button" onClick={() => onSelectSymbol?.(item.symbol)} className="font-bold text-(--accent) hover:underline">
                    {item.symbol}
                  </button>
                  <span className="ml-1 text-[10px] text-(--text-muted)">信心 {Math.round(item.confidence * 100)}%</span>
                  <span className="ml-1 text-(--text-secondary)">— {note ?? '信心未達進場門檻，先觀望等更好的位階 / 訊號。'}</span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
      {(() => {
        // 「llm-fallback-used」= 主力額度用完走備援模型，模型名已在頂部 meta 顯示，這裡不必重複。
        // 其餘才是真正要提醒的系統狀況（資料過舊 / 安全暫停等）。
        const real = section.warnings.filter((w) => w !== 'llm-fallback-used');
        return real.length > 0 ? <div className="mt-2 text-[10px] text-(--warn)">⚠ {real.join('、')}</div> : null;
      })()}
    </section>
  );
}

function ActionSnapshotItem({ item, aiNote, onSelectSymbol }: { item: ReportActionSnapshot; aiNote?: string; onSelectSymbol?: (s: string) => void }) {
  return (
    <li className="rounded border border-(--border) bg-(--bg-card) p-2">
      <div className="flex items-center gap-2 flex-wrap mb-1">
        <button type="button" onClick={() => onSelectSymbol?.(item.symbol)} className="font-bold text-sm text-(--accent) hover:underline">
          {item.symbol}
        </button>
        <span className="text-[10px] px-1.5 py-0.5 rounded border border-(--accent)/40 text-(--accent)">{actionLabel(item.action)}</span>
        <span className="text-[10px] text-(--text-muted)">{actionStatusLabel(item.actionStatus)}</span>
        <span className="ml-auto text-[10px] font-bold tnum text-(--text-secondary)">信心 {Math.round(item.confidence * 100)}%</span>
      </div>
      <div className="text-[11px] text-(--text-secondary) leading-relaxed">
        進 {item.entry ?? '—'} / 停 {item.stop ?? '—'} / T1 {item.t1 ?? '—'} / T2 {item.t2 ?? '—'}
        <br />買 {item.quantity} {quantityUnitLabel(item.quantityUnit)}，投入約 {money(item.notional)}，停損最多虧 {money(item.maxLossAmount)}；淨報酬 {pctPoints(item.expectedNetReturn?.[0])} ~ {pctPoints(item.expectedNetReturn?.[1])}
      </div>
      {aiNote && (
        <div className="mt-1 pt-1 border-t border-(--border) text-[11px] text-(--accent-deep) leading-relaxed flex items-start gap-1">
          <span className="shrink-0">🐧</span><span>{aiNote}</span>
        </div>
      )}
      {item.reasonsTop3.length > 0 && <div className="mt-1 text-[10px] text-(--text-muted)">理由：{item.reasonsTop3.slice(0, 2).join('；')}</div>}
      {item.failureConditions.length > 0 && <div className="mt-1 text-[10px] text-(--warn)">失敗條件：{item.failureConditions.slice(0, 2).join('；')}</div>}
    </li>
  );
}

/**
 * 報告區塊統一標題列：棕色標題 + 右側 meta，跟全站 card-h / bar-title 棕色系統一致。
 */
function ReportHeading({ icon, title, extra }: { icon: string; title: string; extra?: string }) {
  return (
    <div className="flex items-baseline justify-between gap-2 flex-wrap mb-2">
      <h4 className="font-bold text-(--accent-deep) flex items-center gap-1.5" style={{ fontSize: 'var(--fs-12)' }}>
        <span>{icon}</span>{title}
      </h4>
      {extra && <span className="text-[10px] text-(--text-muted) font-semibold">{extra}</span>}
    </div>
  );
}

/**
 * 報告重點摘要列（Hero）：把整份報告最關鍵的 4 個結論放最上面一眼看完，
 * 解決「看不到重點」——使用者不用捲完整份報告才知道今天該不該動、賺賠如何。
 */
function ReportHero({ report }: { report: DailyReport }) {
  const ta = report.todayAction;
  const weekly = report.review?.weekly;
  const actionable = ta?.actions.filter((a) => a.source !== 'watchlist').length ?? 0;
  const marketLabel = ta ? marketActionLabel(ta.marketAction) : '—';
  const marketTone = ta?.marketAction === 'trade' ? 'var(--up)' : ta?.marketAction === 'no-trade' ? 'var(--text-muted)' : 'var(--warn)';
  const ret = ta?.expectedNetReturnRange;
  const retText = ret ? `${pctPoints(ret[0])} ~ ${pctPoints(ret[1])}` : '—';
  const winText = weekly && weekly.resolved > 0 ? `${pct(weekly.hitRate)}` : '—';
  const winSub = weekly && weekly.resolved > 0 ? `本週 ${weekly.hits} 勝 ${weekly.misses} 敗` : '樣本不足';

  return (
    <div className="rounded-xl border-2 border-(--border-strong) bg-gradient-to-br from-(--ice) to-(--bg-card) p-3 grid grid-cols-2 md:grid-cols-4 gap-2">
      <HeroCell label="今日市場動作" value={marketLabel} valueColor={marketTone} />
      <HeroCell label="可執行新單" value={`${actionable}`} sub={actionable > 0 ? '檔' : '坐穩觀望'} />
      <HeroCell label="預期淨報酬" value={retText} valueColor={ret && ret[0] >= 0 ? 'var(--up)' : 'var(--down)'} />
      <HeroCell label="近期命中率" value={winText} sub={winSub} />
    </div>
  );
}

function HeroCell({ label, value, sub, valueColor }: { label: string; value: string; sub?: string; valueColor?: string }) {
  return (
    <div className="min-w-0">
      <div className="text-[10px] font-semibold text-(--text-muted) uppercase tracking-wider">{label}</div>
      <div className="font-extrabold tnum leading-tight truncate" style={{ fontSize: 'var(--fs-bar-sub)', color: valueColor ?? 'var(--accent-deep)' }} title={value}>{value}</div>
      {sub && <div className="text-[10px] text-(--text-muted) truncate">{sub}</div>}
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded border border-(--border) bg-(--bg-card) p-2">
      <div className="text-[10px] text-(--text-muted)">{label}</div>
      <div className="text-sm font-bold tnum text-(--text-primary)">{value}</div>
    </div>
  );
}

function IndexCard({ idx }: { idx: IndexSnapshot }) {
  const up = idx.changePct != null && idx.changePct >= 0;
  const cls = idx.changePct == null ? '' : up ? 'text-(--up)' : 'text-(--down)';
  return (
    <div className="p-2 rounded bg-(--bg-base) border border-(--border)">
      <div className="text-[10px] text-(--text-muted)">{idx.name}</div>
      <div className="text-sm font-bold tnum text-(--text-primary)">
        {idx.price != null ? idx.price.toLocaleString(undefined, { maximumFractionDigits: 2 }) : '—'}
      </div>
      <div className={`text-[10px] tnum ${cls}`}>
        {idx.changePct != null ? `${idx.changePct >= 0 ? '+' : ''}${idx.changePct.toFixed(2)}%` : '—'}
      </div>
    </div>
  );
}
